import { Link as RouterLink } from "react-router-dom";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import SettingsIcon from "@mui/icons-material/Settings";
import type { EmailProvider, SmtpTlsMode } from "../api/types";
import { SecretField } from "./common";
import {
  DEFAULT_PORTS,
  MAX_FROM_NAME_CHARS,
  MAX_RECIPIENTS,
  SMTP_TLS_MODES,
  gmailSenderNote,
  type EmailFormCheck,
  type EmailFormState,
  type GmailSenderState,
} from "./emailChannel";

/**
 * The email delivery channel's config form: one channel type, two providers.
 *
 * **SMTP** is a whole transport, because the client brings their own relay — their
 * Google Workspace, their Postmark, the box their IT runs — and every one of those
 * fields has a way of being wrong that otherwise shows up as a failed delivery
 * hours later. The credential half is write-only, the same deal as a Slack webhook
 * URL: the API redacts `username` and `password` on read, so this form can offer to
 * replace them but never to show them. Leaving both blank keeps what is stored.
 *
 * **Gmail** is the opposite shape: recipients, and nothing else. The sender is the
 * one Google account an owner connected for the whole deployment, which is also the
 * only address Gmail will send as — so there is no `from` to get wrong, no password
 * to store, and nothing about the connection for this form to change.
 *
 * That last part is the point of the split below. Recipients are what this dialog
 * edits; the sender is *status*, shown so an operator can see what the channel will
 * send as and told plainly where to go if it needs connecting. Putting a Connect
 * button here is what previously made "connect the company mailbox" a step trapped
 * inside "add a client's delivery channel".
 *
 * Validation and state live in `emailChannel.ts`; this file is the rendering.
 */

export {
  DEFAULT_PORTS,
  EMAIL_PROVIDERS,
  GMAIL_RETURN_STATUSES,
  MAX_RECIPIENTS,
  SMTP_TLS_MODES,
  emailChannelExtra,
  emailFormState,
  gmailReturnMessage,
  gmailSenderNote,
  gmailSenderState,
  parseRecipients,
  readEmailChannel,
  validateEmailForm,
} from "./emailChannel";
export type {
  EmailFormCheck,
  EmailFormField,
  EmailFormState,
  GmailReturnMessage,
  GmailReturnStatus,
  GmailSenderState,
} from "./emailChannel";

const TLS_LABELS: Record<SmtpTlsMode, string> = {
  starttls: "STARTTLS — upgrade after connecting (usual for port 587)",
  tls: "TLS — encrypted from the first byte (usual for port 465)",
  none: "None — cleartext, no encryption",
};

const PROVIDER_LABELS: Record<EmailProvider, string> = {
  smtp: "SMTP relay — the client's own mail server",
  gmail_oauth: "Gmail — send from a connected Google account",
};

/** The provider chooser plus that provider's fields. Rendered inside the owning dialog. */
export function EmailChannelFields({
  state,
  onChange,
  check,
  mode,
  sender,
  legacyAccount,
}: {
  state: EmailFormState;
  onChange: (next: EmailFormState) => void;
  check: EmailFormCheck;
  mode: "create" | "edit";
  /** The deployment's Gmail sender. Status only — this form cannot change it. */
  sender: GmailSenderState;
  /** An account this row named before the sender became global, if it still does. */
  legacyAccount?: string | null;
}) {
  const set = <K extends keyof EmailFormState>(key: K, value: EmailFormState[K]) =>
    onChange({ ...state, [key]: value });
  const { errors } = check;

  return (
    <Stack spacing={2}>
      <TextField
        select
        label="Sending method"
        value={state.provider}
        onChange={(e) => set("provider", e.target.value as EmailProvider)}
        fullWidth
        helperText="Gmail sends from this server's connected Google account. SMTP sends through a relay the client operates."
      >
        {(Object.keys(PROVIDER_LABELS) as EmailProvider[]).map((provider) => (
          <MenuItem key={provider} value={provider}>
            {PROVIDER_LABELS[provider]}
          </MenuItem>
        ))}
      </TextField>

      {state.provider === "gmail_oauth" ? <GmailSenderPanel sender={sender} /> : null}

      {/* Recipients sit under their own heading on a Gmail channel so the two
          halves cannot be confused: above is the mailbox everything is sent
          *from*, which is a server-wide setting; below is who this one client's
          leads go *to*, which is what this dialog edits. */}
      {state.provider === "gmail_oauth" ? (
        <Typography variant="subtitle2" sx={{ pt: 0.5 }}>
          Recipients for this client
        </Typography>
      ) : null}

      <TextField
        label="Recipients"
        value={state.to}
        onChange={(e) => set("to", e.target.value)}
        required
        fullWidth
        multiline
        minRows={2}
        error={errors.to !== undefined}
        helperText={
          errors.to ??
          `One or more addresses, separated by commas, spaces or newlines. Up to ${MAX_RECIPIENTS}; ` +
            "every lead goes to all of them in one message."
        }
      />

      {state.provider === "gmail_oauth" && legacyAccount ? (
        <Alert severity="info">
          This channel still names <strong>{legacyAccount}</strong> from before the sender became a
          single server-wide account. It is not used: mail goes out as the connected sender above.
          Saving this channel clears the leftover.
        </Alert>
      ) : null}

      {state.provider === "smtp" ? (
        <SmtpFields state={state} set={set} check={check} mode={mode} />
      ) : null}
    </Stack>
  );
}

/**
 * The sender, as status. Read-only by design.
 *
 * There is one Google account per deployment and connecting it is an owner-level
 * operation on Settings, not something to do halfway through configuring a client.
 * So this panel says what the channel will send as, and — when there is nothing to
 * send as — links to the one place that can fix it.
 *
 * "Needs reconnect" is not a state this console can detect: a refresh token is
 * revoked at Google, not here, and the first anyone knows of it is a failed
 * delivery. Rather than guess, it reports what the server says is stored.
 */
function GmailSenderPanel({ sender }: { sender: GmailSenderState }) {
  if (sender.kind === "unavailable") {
    return (
      <Alert severity="warning">
        <AlertTitle>{sender.headline}</AlertTitle>
        {sender.detail}
      </Alert>
    );
  }

  const connected = sender.kind === "connected";
  return (
    <Paper variant="outlined" sx={{ p: 2, bgcolor: "background.default" }}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            label={connected ? "sender connected" : "no sender"}
            color={connected ? "success" : "warning"}
            variant="outlined"
          />
          <Typography variant="subtitle2">{sender.headline}</Typography>
        </Stack>

        <Typography variant="body2" color="text.secondary">
          {gmailSenderNote(sender)}
        </Typography>

        <Divider />

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button
            component={RouterLink}
            to="/settings"
            size="small"
            startIcon={<SettingsIcon />}
            variant={connected ? "text" : "contained"}
          >
            {connected ? "Manage email sender" : "Connect the email sender"}
          </Button>
          <Typography variant="caption" color="text.secondary">
            One Google account serves every client. Only an owner can connect or change it.
          </Typography>
        </Stack>
      </Stack>
    </Paper>
  );
}

/** The SMTP transport fields. */
function SmtpFields({
  state,
  set,
  check,
  mode,
}: {
  state: EmailFormState;
  set: <K extends keyof EmailFormState>(key: K, value: EmailFormState[K]) => void;
  check: EmailFormCheck;
  mode: "create" | "edit";
}) {
  const { errors } = check;
  return (
    <Box>
      <Stack spacing={2}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            label="Sender address"
            value={state.from}
            onChange={(e) => set("from", e.target.value)}
            required
            fullWidth
            error={errors.from !== undefined}
            helperText={errors.from ?? "The envelope sender. The relay has to be willing to send as it."}
          />
          <TextField
            label="Sender name"
            value={state.fromName}
            onChange={(e) => set("fromName", e.target.value)}
            fullWidth
            error={errors.fromName !== undefined}
            helperText={errors.fromName ?? `Optional display name, up to ${MAX_FROM_NAME_CHARS} characters.`}
          />
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            label="SMTP host"
            value={state.host}
            onChange={(e) => set("host", e.target.value)}
            required
            fullWidth
            error={errors.host !== undefined}
            helperText={errors.host ?? "Hostname or IP of the relay, e.g. smtp.gmail.com."}
          />
          <TextField
            label="Port"
            value={state.port}
            onChange={(e) => set("port", e.target.value.replace(/\D/g, ""))}
            error={errors.port !== undefined}
            helperText={errors.port ?? `Blank uses ${DEFAULT_PORTS[state.tls]} for this TLS mode.`}
            sx={{ width: { xs: "100%", sm: 160 }, flexShrink: 0 }}
          />
        </Stack>

        <TextField
          select
          label="TLS mode"
          value={state.tls}
          onChange={(e) => set("tls", e.target.value as SmtpTlsMode)}
          fullWidth
          helperText="How the connection is encrypted. STARTTLS suits nearly every hosted relay."
        >
          {SMTP_TLS_MODES.map((mode_) => (
            <MenuItem key={mode_} value={mode_}>
              {TLS_LABELS[mode_]}
            </MenuItem>
          ))}
        </TextField>

        <Typography variant="subtitle2" sx={{ pt: 1 }}>
          SMTP authentication
        </Typography>
        <SecretField
          label="Username"
          value={state.username}
          onChange={(value) => set("username", value)}
          isSet={false}
          helperText={
            mode === "create"
              ? "Optional — leave blank for a relay that needs no login. Stored write-only."
              : "Stored write-only and never returned. Leave blank to keep the stored login."
          }
        />
        <SecretField
          label="Password"
          value={state.password}
          onChange={(value) => set("password", value)}
          isSet={false}
          helperText={
            mode === "create"
              ? "Required if a username is set. Stored write-only."
              : "Stored write-only and never returned. Leave blank to keep the stored password."
          }
        />
        {errors.auth !== undefined ? <Alert severity="error">{errors.auth}</Alert> : null}
        {check.warnings.map((warning) => (
          <Alert key={warning} severity="warning">
            {warning}
          </Alert>
        ))}
        {mode === "edit" ? (
          <Alert severity="info">
            A stored credential cannot be cleared from here — an omitted field is read as "keep it". To move
            this client to a relay that needs no login, delete the channel and add it again.
          </Alert>
        ) : null}
      </Stack>
    </Box>
  );
}
